"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
// Inspired by https://github.com/WebDevSimplified/useful-custom-react-hooks/blob/main/src/14-useOnScreen/useOnScreen.js
const react_1 = require("react");
/**
 * Custom React hook to detect whether an element is within the viewport.
 *
 * @param {RefObject<HTMLElement>} ref - Reference object for the target element to observe.
 * @param {object} [options] - Options for configuring the IntersectionObserver.
 * @param {string} [options.rootMargin='0px'] - Margin around the root (viewport) to track intersections.
 * @param {number | number[]} [options.threshold] - A single threshold value or an array of threshold values
 *        which indicate the percentage of the target element's visibility needed to trigger the callback.
 *
 * @returns {boolean} - Boolean value indicating whether the target element is currently visible in the viewport.
 */
const useOnScreen = (ref, options = { rootMargin: '0px' }) => {
    const [isVisible, setIsVisible] = (0, react_1.useState)(false);
    (0, react_1.useEffect)(() => {
        if (!ref.current) {
            return;
        }
        /**
         * Callback function for IntersectionObserver.
         *
         * @param {IntersectionObserverEntry[]} entries - An array of IntersectionObserverEntry objects describing
         * the intersection between the target element and its root container at a specific moment of transition.
         */
        const callback = ([entry]) => setIsVisible(entry.isIntersecting);
        // Create a new IntersectionObserver instance
        const observer = new IntersectionObserver(callback, options);
        // Start observing the target element
        observer.observe(ref.current);
        // Cleanup function to stop observing the target element when the component unmounts
        return () => {
            if (!ref.current) {
                return;
            }
            observer.unobserve(ref.current);
        };
    }, [ref, options]);
    return isVisible;
};
exports.default = useOnScreen;
